import { useMemo, useState } from 'react'

import type { ThumbnailGroup, ThumbnailItem } from './useThumbnailGroups'

/** 履歴一覧の絞り込み（決定事項2のチップ「すべて」「ピン留め」「お気に入り」） */
export type HistoryFilter = 'all' | 'pinned' | 'favorited'

export interface HistoryFilterCounts {
  /** 履歴（生成画像）の件数。外観カット（現況）は数えない */
  all: number
  pinned: number
  favorited: number
}

/** ピン留めパネル・ピン留めカード列に出す1件（ピン留めした順） */
export interface PinnedHistoryItem {
  item: ThumbnailItem
  /** 「カット1」のようなラベル（外観カットの番号） */
  cutLabel: string
  pinnedAt: Date
}

const matchesFilter = (item: ThumbnailItem, filter: HistoryFilter): boolean => {
  if (filter === 'pinned') return Boolean(item.pinnedAt)
  if (filter === 'favorited') return Boolean(item.favorited)
  return true
}

/**
 * 履歴の絞り込みと、ピン留めした履歴の一覧を導出する。
 *
 * - 絞り込みはカットごとの履歴一覧に適用する。「ピン留め」「お気に入り」では現況（元画像）も隠す
 *   （履歴だけを並べる）。ピン留めパネル・ピン留めカード列は絞り込みの影響を受けない（design.md D7）
 * - ピン留めの並びはpinnedAtの昇順（ピン留めした順。決定事項2）
 */
export const useHistoryFilter = (groups: ThumbnailGroup[]) => {
  const [filter, setFilter] = useState<HistoryFilter>('all')

  const counts = useMemo<HistoryFilterCounts>(() => {
    const processed = groups.flatMap(group => group.items.filter(item => item.type === 'processed'))
    return {
      all: processed.length,
      pinned: processed.filter(item => item.pinnedAt).length,
      favorited: processed.filter(item => item.favorited).length,
    }
  }, [groups])

  const filteredGroups = useMemo<ThumbnailGroup[]>(() => {
    if (filter === 'all') return groups
    return groups.map(group => ({
      ...group,
      items: group.items.filter(item => item.type === 'processed' && matchesFilter(item, filter)),
    }))
  }, [groups, filter])

  const pinnedItems = useMemo<PinnedHistoryItem[]>(
    () =>
      groups
        .flatMap((group, index) =>
          group.items
            .filter(item => item.type === 'processed' && item.pinnedAt)
            .map(item => ({ item, cutLabel: `カット${index + 1}`, pinnedAt: item.pinnedAt! })),
        )
        .sort((a, b) => a.pinnedAt.getTime() - b.pinnedAt.getTime()),
    [groups],
  )

  return { filter, setFilter, counts, filteredGroups, pinnedItems }
}
